import { z } from 'zod';
import { requiredUuidString } from '../zod-helpers/index.js';
import { MAX_WORK_ORDERS_PER_LINE } from './split-quantities.js';
// Bounded by Temporal's 2MB payload limit rather than by the theoretical maximum, which per-ticket
// answers make 200 lines x 50 work orders x 10 groups -- roughly 12MB of JSON.
export const MAX_OPTION_GROUP_CHOICES = 5000;
export const MAX_OPTION_GROUP_CHOICES_MESSAGE = `A release can carry at most ${MAX_OPTION_GROUP_CHOICES} option choices.`;
// WHY: release scans the whole array once per unreleased line, so an unbounded one is quadratic work under FOR UPDATE locks.
export const groupChoicesSchema = z
    .array(z.object({
    manufacturingOrderItemId: requiredUuidString('Each option choice must name a part line.', 'Invalid part line on an option choice.'),
    bomItemGroupId: requiredUuidString('Each option choice must name an option group.', 'Invalid option group on an option choice.'),
    bomItemId: requiredUuidString('Each option choice must name an option.', 'Invalid option on an option choice.'),
    // Absent answers every work order the line is cut into, which is what a release started
    // before per-work-order answers existed still means when its workflow replays.
    ticketIndex: z
        .number()
        .int('A work order number on an option choice must be a whole number.')
        .min(0, 'A work order number on an option choice cannot be negative.')
        .max(MAX_WORK_ORDERS_PER_LINE - 1, `A line is cut into at most ${MAX_WORK_ORDERS_PER_LINE} work orders.`)
        .optional(),
}))
    .max(MAX_OPTION_GROUP_CHOICES, MAX_OPTION_GROUP_CHOICES_MESSAGE)
    // WHY: resolveOptionGroups rejects a second answer for the same group deterministically, but only
    // once release is already running under lock — reject it here so issue does too, while the order
    // is still draft, instead of stranding it ISSUED with a failed release.
    .refine((choices) => {
    const seen = new Set();
    for (const choice of choices) {
        const key = `${choice.manufacturingOrderItemId}:${choice.ticketIndex ?? 'all'}:${choice.bomItemGroupId}`;
        if (seen.has(key))
            return false;
        seen.add(key);
    }
    return true;
}, { message: 'Each option group on a work order can be answered only once.' })
    .refine((choices) => {
    const lineWide = new Set(choices
        .filter((choice) => choice.ticketIndex === undefined)
        .map((choice) => `${choice.manufacturingOrderItemId}:${choice.bomItemGroupId}`));
    return !choices.some((choice) => choice.ticketIndex !== undefined &&
        lineWide.has(`${choice.manufacturingOrderItemId}:${choice.bomItemGroupId}`));
}, {
    message: 'An option group is answered either once for the whole line or once per work order, not both.',
});
//# sourceMappingURL=group-choices.js.map